import { call, fork, put, select, take, takeLatest } from '@redux-saga/core/effects';
import { catchError } from '@utils/sentry';
import { GET_STEP_DETAILS } from '@containers/App/queries';
import { REGISTER_WORKFLOW } from '@containers/PublicAssessment/queries';
import fetchData from '@utils/fetchData';
import postData from '@utils/postData';
import { getRuntimeConfigSync } from '@utils/runtimeConfig';
import { selectQueryParams } from '@containers/Auth/selectors';
import { setRegistrationStatus, setStepDetailsError, storeStepDetails } from './slice';
import { saveFormDataSuccess } from '@containers/Standalone/slice';
const parseStepId = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};
const DEFAULT_CX_V2_BASE_URL = 'https://cx-v2.talview.com';
export const getAssessmentBaseUrl = () => {
    try {
        const config = getRuntimeConfigSync();
        return config.CANDIDATE_X_URL || DEFAULT_CX_V2_BASE_URL;
    }
    catch (error) {
        catchError({
            title: 'getAssessmentBaseUrl',
            error: error,
        });
        return DEFAULT_CX_V2_BASE_URL;
    }
};
const getSafeAssessmentInviteBaseUrl = (baseUrl) => {
    try {
        return new URL(baseUrl).toString();
    }
    catch (error) {
        catchError({
            title: 'buildAssessmentInviteUrl',
            error: error,
        });
        return DEFAULT_CX_V2_BASE_URL;
    }
};
export const buildAssessmentInviteUrl = (testInstanceId, stepId, baseUrl) => {
    const query = new URLSearchParams({
        test_instance_id: String(testInstanceId),
        step_id: String(stepId),
    });
    const inviteUrl = new URL('/assessment/invite', getSafeAssessmentInviteBaseUrl(baseUrl));
    inviteUrl.search = query.toString();
    return inviteUrl.toString();
};
const shouldRedirectToAssessmentInvite = (response) => response?.next_step_type?.toLowerCase() === 'assessment' &&
    Number.isInteger(response?.next_workflow_candidate_step?.step_id) &&
    Number(response?.next_workflow_candidate_step?.step_id) > 0 &&
    Number.isInteger(response?.test_instance_id) &&
    Number(response?.test_instance_id) > 0;
export function* getStepDetailsWorker(action) {
    try {
        const { step_id } = action.payload;
        if (!step_id)
            throw new Error('Missing step_id');
        const response = yield call(fetchData, {
            queryString: GET_STEP_DETAILS,
            queryVariables: { step_id },
            queryKey: 'canx_get_step_details',
        });
        if (response) {
            yield put(storeStepDetails(response));
            if (action.payload.callback?.onSuccess) {
                yield call(action.payload.callback.onSuccess, response);
            }
        }
    }
    catch (error) {
        // Without this the slice's `loading` flag never flips back to false on failure — the UI
        // (index.tsx: `if (loading || !isWindowEvaluated) return <spinner>`) hangs on the spinner
        // indefinitely instead of surfacing the error screen that already exists for this case.
        yield put(setStepDetailsError(error?.message || 'Failed to load step details'));
        yield call(catchError, {
            title: 'getStepDetailsWorker',
            error: error,
        });
        if (action.payload.callback?.onError) {
            yield call(action.payload.callback.onError, error);
        }
    }
}
export function* handleFormDataSuccess(action) {
    try {
        const formData = action.payload;
        const source = formData.source;
        if (source !== '/public-assessment') {
            return;
        }
        if (!formData?.id) {
            throw new Error('Form data missing id');
        }
        yield put(setRegistrationStatus('loading'));
        // Get the current step details from the store
        const { stepDetail } = (yield select((state) => state.publicAssessment));
        let stepId = parseStepId(stepDetail?.id);
        // A reload empties the in-memory store, but Auth persists the landing params to localStorage,
        // so recover the step id from there rather than stranding the candidate (CX-4584).
        if (!stepId) {
            const queryParams = (yield select(selectQueryParams));
            stepId = parseStepId(queryParams?.step_id);
        }
        if (!stepId) {
            throw new Error('Step details not found in the store');
        }
        const variables = {
            step_id: stepId,
            form_instance_id: formData.id,
            first_name: formData.form_version?.form?.candidateFirstName,
            last_name: formData.form_version?.form?.candidateLastName,
            email: formData.form_version?.form?.email,
            phone_number: formData.form_version?.form?.candidatePhoneNumber,
        };
        const response = yield call(postData, {
            queryString: REGISTER_WORKFLOW,
            payload: variables,
            spreadPayload: true,
        });
        const registerResponse = response?.data?.canx_workflow_register;
        if (registerResponse && shouldRedirectToAssessmentInvite(registerResponse)) {
            yield put(setRegistrationStatus('redirecting'));
            const testInstanceId = registerResponse.test_instance_id;
            const nextStepId = registerResponse.next_workflow_candidate_step.step_id;
            const assessmentUrl = buildAssessmentInviteUrl(testInstanceId, nextStepId, window.location.origin);
            yield call([window.location, 'assign'], assessmentUrl);
            return;
        }
        yield put(setRegistrationStatus('complete'));
        if (action.payload.callback?.onSuccess) {
            yield call(action.payload.callback.onSuccess, response);
        }
    }
    catch (error) {
        yield put(setRegistrationStatus('complete'));
        yield call(catchError, {
            title: 'handleFormDataSuccess',
            error: error,
        });
        if (action.payload.callback?.onError) {
            yield call(action.payload.callback.onError, error);
        }
    }
}
function* watchFormDataSuccess() {
    try {
        while (true) {
            const action = (yield take(saveFormDataSuccess.type));
            yield call(handleFormDataSuccess, action);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'watchFormDataSuccess',
            error: error,
        });
    }
}
function* getStepDetailsWatcher() {
    yield takeLatest('publicAssessment/fetchStepDetails', getStepDetailsWorker);
}
export function* publicAssessmentRootSaga() {
    yield fork(watchFormDataSuccess);
    yield fork(getStepDetailsWatcher);
}
